/**
 * Australian Warfighter Coffee — build your box, by weight (Empire port).
 *
 * Every addable row is served with its variant id, its price in cents and its
 * shipping weight in grams. This element keeps the per-row counts, sums those
 * server-rendered integers, and refuses any add that would take the box past
 * `data-max-grams`. It never derives a price or a weight from anything but a
 * number the page was served with.
 *
 * The limit is a shipping promise: Australian postage is banded by cart weight,
 * and the page states what the band covering a full box costs.
 *
 * Grind confirmation: before a box holding any grind-dependent bag goes to the
 * cart, the customer confirms the grind. Switching — from there or from the
 * grind picker — carries each count to the same coffee and size in the other
 * grind, and names anything that grind does not stock.
 *
 * Empire refreshes its header cart count from a `cartcount:update` event on
 * window carrying the cart JSON — the same event awc-bundle fires.
 */
(function () {
  if (customElements.get('awc-box')) return;

  /** Formats minor units the way the `money` filter does. Mirrors awc-bundle.js. */
  function formatMoney(cents, format) {
    var template = format || '${{amount}}';

    function withDelimiters(value, precision, thousands, decimal) {
      var fixed = (value / 100).toFixed(precision);
      var parts = fixed.split('.');
      var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts[1] ? whole + decimal + parts[1] : whole;
    }

    return template.replace(/\{\{\s*(\w+)\s*\}\}/, function (_, placeholder) {
      switch (placeholder) {
        case 'amount_no_decimals':
          return withDelimiters(cents, 0, ',', '.');
        case 'amount_with_comma_separator':
          return withDelimiters(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return withDelimiters(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return withDelimiters(cents, 2, "'", '.');
        default:
          return withDelimiters(cents, 2, ',', '.');
      }
    });
  }

  function formatWeight(grams) {
    if (grams < 1000) return grams + ' g';

    return (grams / 1000).toLocaleString('en-AU', { maximumFractionDigits: 2 }) + ' kg';
  }

  function fill(template, values) {
    return String(template || '').replace(/\{(\w+)\}/g, function (match, name) {
      return Object.prototype.hasOwnProperty.call(values, name) ? values[name] : match;
    });
  }

  class AwcBox extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-box-form]');
      this.button = this.form && this.form.querySelector('button[type="submit"]');
      if (!this.form || !this.button) return;

      this.ctaCount = this.form.querySelector('[data-cta-count]');
      this.summaryCount = this.form.querySelector('[data-summary-count]');
      this.summaryTotal = this.form.querySelector('[data-summary-total]');
      this.errorField = this.form.querySelector('[data-box-error]');
      this.successField = this.form.querySelector('[data-box-success]');
      this.statusField = this.form.querySelector('[data-box-status]');
      this.capNote = this.form.querySelector('[data-cap-note]');
      this.meter = this.querySelector('[data-meter]');
      this.bar = this.querySelector('[data-bar]');
      this.barFill = this.querySelector('[data-bar-fill]');
      this.weightOut = this.querySelector('[data-weight]');
      this.remainingOut = this.querySelector('[data-remaining]');

      // Postage guide. The bands are the section's copy of Settings → Shipping.
      this.postageOut = this.querySelector('[data-postage]');
      this.bandSegments = Array.prototype.slice.call(this.querySelectorAll('[data-band]'));
      this.bands = this.parseBands(this.dataset.bands);
      this.postageNow = this.dataset.postageNow || 'Postage {price}';
      this.postageStart = this.dataset.postageStart || 'Postage from {price}';
      this.postageFit = this.dataset.postageFit || 'Room for another {size} at no extra postage';
      this.postageNext = this.dataset.postageNext || 'Anything more takes postage to {price}';

      this.confirmBox = this.form.querySelector('[data-confirm]');
      this.confirmTitleOut = this.form.querySelector('[data-confirm-title]');
      this.confirmBodyOut = this.form.querySelector('[data-confirm-body]');
      this.confirmEnabled = this.dataset.confirmGrind === 'true' && Boolean(this.confirmBox);
      this.confirmTitle = this.dataset.confirmTitle || "You've picked {grind}. Are you sure?";
      this.confirmBody = this.dataset.confirmBody || 'Every bag in this box will be {description}.';

      this.maxGrams = Number(this.dataset.maxGrams) || Infinity;
      this.emptyNote = this.summaryCount ? this.summaryCount.textContent.trim() : '';
      this.countNote = this.dataset.countNote || '{count} items in the box';
      this.remainingNote = this.dataset.remainingNote || '{weight} left';
      this.grindProperty = this.dataset.grindProperty || '';

      var template = this.querySelector('[data-money-format]');
      this.moneyFormat = (template && template.content.textContent.trim()) || '${{amount}}';

      this.onChange = this.onChange.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onInput = this.onInput.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onSubmit = this.onSubmit.bind(this);

      this.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onClick);
      this.addEventListener('input', this.onInput);
      this.addEventListener('keydown', this.onKeydown);
      this.form.addEventListener('submit', this.onSubmit);

      this.sync();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('input', this.onInput);
      this.removeEventListener('keydown', this.onKeydown);
      if (this.form) this.form.removeEventListener('submit', this.onSubmit);
    }

    panel() {
      var selected = this.querySelector('.awc-box__grind-input:checked');
      if (!selected) return this.querySelector('.awc-box__panel');

      return this.querySelector('.awc-box__panel[data-grind="' + CSS.escape(selected.value) + '"]');
    }

    /** Addable rows in the visible panel. Sold-out and unweighed rows carry no data. */
    rows() {
      var panel = this.panel();
      return panel ? Array.prototype.slice.call(panel.querySelectorAll('[data-line]')) : [];
    }

    countOf(row) {
      var input = row.querySelector('[data-count]');
      var value = parseInt(input ? input.value : '0', 10);
      if (!isFinite(value)) return 0;

      return Math.min(Math.max(value, 0), Number(input && input.max) || 0);
    }

    gramsOf(row) {
      return Number(row.dataset.grams) || 0;
    }

    /** Returns the count actually set, after clamping. */
    setCount(row, next) {
      var input = row.querySelector('[data-count]');
      if (!input) return 0;

      // Clamp against the per-row maximum and against the weight left in the
      // box, so neither a click nor a typed number can overfill it.
      var self = this;
      var grams = this.gramsOf(row);
      var elsewhere = this.rows().reduce(function (sum, other) {
        return other === row ? sum : sum + self.gramsOf(other) * self.countOf(other);
      }, 0);
      var fits = grams > 0 ? Math.floor((this.maxGrams - elsewhere) / grams) : 0;
      var max = Math.min(Number(input.max) || 0, fits);
      var clamped = Math.min(Math.max(next, 0), Math.max(max, 0));
      input.value = String(clamped);

      var add = row.querySelector('[data-add]');
      var stepper = row.querySelector('[data-stepper]');
      if (add) add.hidden = clamped > 0;
      if (stepper) stepper.hidden = clamped === 0;
      row.dataset.chosen = String(clamped > 0);

      return clamped;
    }

    onChange(event) {
      var target = event.target;
      if (!(target instanceof HTMLInputElement)) return;

      if (target.classList.contains('awc-box__grind-input')) {
        this.hideConfirm();
        this.setMessage(null, false);
        this.setStatus(this.droppedNote(this.showGrind(target.value)));
        return;
      }

      if (target.hasAttribute('data-count')) this.sync();
    }

    onInput(event) {
      var target = event.target;
      if (!(target instanceof HTMLInputElement) || !target.hasAttribute('data-count')) return;

      this.hideConfirm();
      var row = target.closest('[data-line]');
      if (row) this.setCount(row, this.countOf(row));

      this.sync();
    }

    onKeydown(event) {
      if (event.key !== 'Escape' || !this.confirmBox || this.confirmBox.hidden) return;

      this.hideConfirm();
      this.button.focus();
    }

    onClick(event) {
      var origin = event.target instanceof Element ? event.target : null;
      if (!origin) return;

      if (origin.closest('[data-confirm-yes]')) {
        this.hideConfirm();
        this.add();
        return;
      }

      var switcher = origin.closest('[data-confirm-switch]');
      if (switcher) {
        this.hideConfirm();
        var dropped = this.showGrind(switcher.dataset.confirmSwitch);
        var current = this.panel();
        var label = current ? current.dataset.grindLabel : '';
        this.setStatus(
          ['Switched to ' + label + '. Check your box, then add it to your cart.', this.droppedNote(dropped)]
            .filter(Boolean)
            .join(' ')
        );
        this.button.focus();
        return;
      }

      var row = origin.closest('[data-line]');
      if (!row) return;

      if (origin.closest('[data-add]')) {
        this.hideConfirm();
        this.setCount(row, 1);
        this.sync();
        var plus = row.querySelector('[data-step="1"]');
        if (plus) plus.focus();
        return;
      }

      var step = origin.closest('[data-step]');
      if (step) {
        this.hideConfirm();
        var next = this.countOf(row) + Number(step.dataset.step);
        this.setCount(row, next);
        this.sync();

        if (next <= 0) {
          var addButton = row.querySelector('[data-add]');
          if (addButton) addButton.focus();
        }
      }
    }

    /**
     * Shows the panel for `grind` and carries every chosen count across to the
     * row with the same key there — the same coffee in the same size. Returns
     * the names of rows that could not be carried, in full or part.
     */
    showGrind(grind) {
      var self = this;
      var from = this.querySelector('.awc-box__panel:not([hidden])');
      var radio = this.querySelector('.awc-box__grind-input[value="' + CSS.escape(grind) + '"]');
      if (radio) radio.checked = true;

      var to = this.panel();
      if (!to || to === from) {
        this.sync();
        return [];
      }

      var moves = Array.prototype.slice
        .call(from ? from.querySelectorAll('[data-line]') : [])
        .map(function (row) {
          return { key: row.dataset.key, name: row.dataset.name, count: self.countOf(row), row: row };
        })
        .filter(function (move) {
          return move.count > 0;
        });

      moves.forEach(function (move) {
        self.setCount(move.row, 0);
      });

      var panels = this.querySelectorAll('.awc-box__panel');
      for (var i = 0; i < panels.length; i++) panels[i].hidden = panels[i] !== to;

      var dropped = [];
      moves.forEach(function (move) {
        var target = to.querySelector('[data-line][data-key="' + CSS.escape(move.key) + '"]');
        var carried = target ? self.setCount(target, move.count) : 0;
        if (carried < move.count) dropped.push(move.name);
      });

      this.sync();
      return dropped;
    }

    droppedNote(dropped) {
      if (!dropped || dropped.length === 0) return '';

      var current = this.panel();
      var label = current ? current.dataset.grindLabel : 'this grind';
      return 'Not available as ' + label + ', so taken out: ' + dropped.join(', ') + '.';
    }

    lines() {
      var self = this;
      return this.rows()
        .map(function (row) {
          return {
            id: Number(row.dataset.variant),
            quantity: self.countOf(row),
            price: Number(row.dataset.price),
            grams: self.gramsOf(row),
            followsGrind: row.hasAttribute('data-follows-grind'),
          };
        })
        .filter(function (line) {
          return line.quantity > 0 && isFinite(line.id) && isFinite(line.price);
        });
    }

    sync() {
      var lines = this.lines();
      var count = 0;
      var subtotal = 0;
      var grams = 0;
      lines.forEach(function (line) {
        count += line.quantity;
        subtotal += line.price * line.quantity;
        grams += line.grams * line.quantity;
      });

      if (this.summaryCount) {
        this.summaryCount.textContent = count === 0 ? this.emptyNote : this.countNote.replace('{count}', String(count));
      }
      if (this.summaryTotal) this.summaryTotal.textContent = formatMoney(subtotal, this.moneyFormat);
      if (this.ctaCount) this.ctaCount.textContent = count > 0 ? '· ' + formatWeight(grams) : '';

      this.syncMeter(grams);
      this.button.disabled = count === 0 || grams > this.maxGrams;
    }

    /** Any row heavier than what is left goes dead. When nothing fits, the box is full. */
    syncMeter(grams) {
      var left = Math.max(this.maxGrams - grams, 0);
      var percent = isFinite(this.maxGrams) ? Math.min((grams / this.maxGrams) * 100, 100) : 0;

      if (this.weightOut) this.weightOut.textContent = grams === 0 ? '0 kg' : formatWeight(grams);
      if (this.barFill) this.barFill.style.width = percent + '%';
      if (this.bar) {
        this.bar.setAttribute('aria-valuenow', String(grams));
        this.bar.setAttribute('aria-valuetext', formatWeight(grams));
      }
      if (this.remainingOut) this.remainingOut.textContent = this.remainingNote.replace('{weight}', formatWeight(left));

      var anythingFits = false;
      var rows = this.rows();
      for (var i = 0; i < rows.length; i++) {
        var input = rows[i].querySelector('[data-count]');
        var atRowMax = this.countOf(rows[i]) >= (Number(input && input.max) || 0);
        var tooHeavy = this.gramsOf(rows[i]) > left;
        if (!tooHeavy && !atRowMax) anythingFits = true;

        var add = rows[i].querySelector('[data-add]');
        var plus = rows[i].querySelector('[data-step="1"]');
        if (add) add.disabled = tooHeavy;
        if (plus) plus.disabled = tooHeavy || atRowMax;
      }

      var full = grams > 0 && !anythingFits;
      if (this.meter) this.meter.dataset.full = String(full);
      if (this.capNote) this.capNote.hidden = !full;

      this.syncPostage(grams, left);
    }

    /** Bands in the order the section lists them: [{max, price}]. */
    parseBands(json) {
      try {
        var bands = JSON.parse(json || '[]');
        return Array.isArray(bands)
          ? bands.filter(function (band) {
              return isFinite(band.max) && isFinite(band.price);
            })
          : [];
      } catch (e) {
        return [];
      }
    }

    /**
     * States the postage for the box as built, then the most useful next fact:
     * the biggest bag that still fits at that price, or failing that, what the
     * next step costs. Every figure is one of the section's bands.
     */
    syncPostage(grams, left) {
      if (!this.postageOut || this.bands.length === 0) return;

      var self = this;
      var index = -1;
      for (var i = 0; i < this.bands.length; i++) {
        if (grams <= this.bands[i].max) {
          index = i;
          break;
        }
      }

      this.bandSegments.forEach(function (segment, position) {
        segment.setAttribute('aria-current', String(grams > 0 && position === index));
      });

      if (index === -1) {
        this.postageOut.hidden = true;
        return;
      }

      var band = this.bands[index];
      this.postageOut.hidden = false;

      if (grams === 0) {
        this.postageOut.textContent = fill(this.postageStart, { price: formatMoney(band.price, this.moneyFormat) });
        return;
      }

      var room = Math.min(band.max - grams, left);
      var fit = this.rows()
        .filter(function (row) {
          var input = row.querySelector('[data-count]');
          return self.gramsOf(row) <= room && self.countOf(row) < (Number(input && input.max) || 0);
        })
        .sort(function (a, b) {
          return self.gramsOf(b) - self.gramsOf(a);
        })[0];

      var parts = [fill(this.postageNow, { price: formatMoney(band.price, this.moneyFormat) })];
      var next = this.bands[index + 1];

      if (fit) {
        parts.push(fill(this.postageFit, { size: fit.dataset.size || formatWeight(this.gramsOf(fit)) }));
      } else if (next && left > 0) {
        parts.push(fill(this.postageNext, { price: formatMoney(next.price, this.moneyFormat) }));
      }

      this.postageOut.textContent = parts.join(' · ');
    }

    setMessage(error, success) {
      if (this.errorField) {
        this.errorField.textContent = error || '';
        this.errorField.hidden = !error;
      }
      if (this.successField) this.successField.hidden = !success;
    }

    setStatus(message) {
      if (!this.statusField) return;

      this.statusField.textContent = message || '';
      this.statusField.hidden = !message;
    }

    showConfirm() {
      var current = this.panel();
      var label = current ? current.dataset.grindLabel : '';
      var description = (current && current.dataset.grindDescription) || label;
      var values = { grind: label, description: description };

      if (this.confirmTitleOut) this.confirmTitleOut.textContent = fill(this.confirmTitle, values);
      if (this.confirmBodyOut) this.confirmBodyOut.textContent = fill(this.confirmBody, values);

      // Offer a switch to every grind except the one already picked.
      var switchers = this.confirmBox.querySelectorAll('[data-confirm-switch]');
      for (var i = 0; i < switchers.length; i++) {
        switchers[i].hidden = !current || switchers[i].dataset.confirmSwitch === current.dataset.grind;
      }

      this.setMessage(null, false);
      this.setStatus(null);
      this.confirmBox.hidden = false;
      var yes = this.confirmBox.querySelector('[data-confirm-yes]');
      if (yes) yes.focus();
    }

    hideConfirm() {
      if (this.confirmBox) this.confirmBox.hidden = true;
    }

    onSubmit(event) {
      event.preventDefault();

      var lines = this.lines();
      var grams = lines.reduce(function (sum, line) {
        return sum + line.grams * line.quantity;
      }, 0);

      if (lines.length === 0) {
        this.setMessage('Add at least one coffee first.', false);
        return;
      }
      if (grams > this.maxGrams) {
        this.setMessage('This box is over the weight limit. Remove something and try again.', false);
        return;
      }

      // Instant has no grind, so a box of only instant has nothing to confirm.
      var needsConfirm = lines.some(function (line) {
        return line.followsGrind;
      });
      if (this.confirmEnabled && needsConfirm) {
        this.showConfirm();
        return;
      }

      this.add();
    }

    add() {
      var lines = this.lines();
      if (lines.length === 0) return;

      // The grind is finer than the variant: espresso, plunger and cold brew
      // all buy the same ground bag, so the style rides on the line. Instant
      // has no grind, so it is added plain.
      var current = this.panel();
      var grind = (current && current.dataset.grindLabel) || '';
      var property = this.grindProperty;
      var items = lines.map(function (line) {
        var item = { id: line.id, quantity: line.quantity };
        if (property && grind && line.followsGrind) {
          item.properties = {};
          item.properties[property] = grind;
        }
        return item;
      });
      var routes = (window.Theme && window.Theme.routes) || {};
      var addUrl = (routes.cart_add_url || '/cart/add') + '.js';
      var cartUrl = routes.cart_url || '/cart';
      var self = this;

      this.setMessage(null, false);
      this.setStatus(null);
      this.button.disabled = true;

      fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ items: items }),
      })
        .then(function (response) {
          // Shopify's bot protection answers a burst of adds with a 429 HTML page.
          if (response.status === 429) {
            throw new Error('Too many attempts in a row. Wait a minute, then add your box again.');
          }
          return response
            .json()
            .catch(function () {
              return {};
            })
            .then(function (result) {
              // The AJAX cart answers a rejected add with a `status` field.
              if (!response.ok || result.status) {
                throw new Error(result.description || result.message || 'Could not add this box to the cart.');
              }
            });
        })
        .then(function () {
          // The box is in the cart from here on. Refreshing the header count is a
          // courtesy — if that fetch fails, say it worked, because it did.
          self.setMessage(null, true);

          return fetch(cartUrl + '.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
            .then(function (response) {
              return response.json();
            })
            .then(function (cart) {
              window.dispatchEvent(new CustomEvent('cartcount:update', { detail: cart }));
            })
            .catch(function (error) {
              console.warn('[awc-box] added, but could not refresh the cart count', error);
            });
        })
        .catch(function (error) {
          console.error('[awc-box] add to cart failed', error);
          self.setMessage(error && error.message ? error.message : 'Could not reach the cart. Please try again.', false);
        })
        .then(function () {
          self.sync();
        });
    }
  }

  customElements.define('awc-box', AwcBox);
})();
